import { useMemo, useRef } from 'react';

import {
    useClick,
    useDismiss,
    useInteractions,
    useListNavigation,
    useRole,
    useTypeahead
} from '@floating-ui/react';
import { isNil } from 'ramda';

import { SELECT_DEFAULT_OPTION_FILTER_PROP } from '../../Select.constants';
import { isOptionGroup } from '../../Select.helpers';

import type { SelectFloatingReturn } from './useSelectFloating';
import type { SelectOptionsReturn } from './useSelectOptions';
import type { SelectSelectionReturn } from './useSelectSelection';
import type { SelectOption } from '../../Select.types';

type UseSelectFloatingInteractionsProps = {
    floating: SelectFloatingReturn;
    isTypeaheadEnabled: boolean | undefined;
    isTypingRef: React.MutableRefObject<boolean>;
    selection: SelectSelectionReturn;
    options: SelectOptionsReturn;
    optionFilterProp: keyof SelectOption;
};

export const useSelectFloatingInteractions = ({
    floating,
    isTypeaheadEnabled,
    isTypingRef,
    selection,
    options,
    optionFilterProp
}: UseSelectFloatingInteractionsProps) => {
    const listContent = useMemo(
        () =>
            options.displayOptions.map((item) => {
                return (
                    (optionFilterProp && item[optionFilterProp]) ??
                    item[SELECT_DEFAULT_OPTION_FILTER_PROP] ??
                    ''
                );
            }),
        [options.displayOptions, optionFilterProp]
    );
    const groupsIndices = useMemo(
        () =>
            options.displayOptions.reduce(
                (acc, item, index) => (isOptionGroup(item) ? [...acc, index] : acc),
                [] as number[]
            ),
        [options.displayOptions]
    );
    const disabledIndices = useMemo(
        () =>
            options.displayOptions.reduce(
                (acc, item, index) => (item.disabled ? [...acc, index] : acc),
                [...groupsIndices] as number[]
            ),
        [options.displayOptions, groupsIndices]
    );
    const listContentRef = useRef<Array<string | null>>(listContent);
    const click = useClick(floating.context, {
        event: 'click'
    });
    const role = useRole(floating.context, { role: 'listbox' });
    const dismiss = useDismiss(floating.context);
    const virtualItemRef = useRef(null);

    const listNavigation = useListNavigation(floating.context, {
        listRef: options.optionsElementsRef,
        activeIndex: options.activeIndex,
        selectedIndex: options.selectedIndex,
        onNavigate: (newActiveIndex) => {
            if (isNil(newActiveIndex) && isTypingRef.current) {
                return;
            }
            options.setActiveIndex(newActiveIndex);
        },
        focusItemOnHover: false,
        virtual: true,
        virtualItemRef,
        loop: true
    });

    const typeahead = useTypeahead(floating.context, {
        listRef: listContentRef,
        activeIndex: options.activeIndex,
        enabled: isTypeaheadEnabled,
        onMatch: (newActiveIndex) => {
            if (newActiveIndex == null || disabledIndices.includes(newActiveIndex)) {
                if (newActiveIndex != null) {
                    options.setActiveIndex(newActiveIndex);
                }
                return;
            }
            options.setActiveIndex(newActiveIndex);
            selection.selectOptionByIndex(newActiveIndex);
        },
        onTypingChange(isTyping) {
            isTypingRef.current = isTyping;
        }
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        click,
        role,
        dismiss,
        listNavigation,
        typeahead
    ]);

    const contextProps = useMemo(
        () => ({
            getReferenceProps,
            getFloatingProps,
            getItemProps
        }),
        [getReferenceProps, getFloatingProps, getItemProps]
    );

    return contextProps;
};

export type SelectFloatingInteractionsReturn = ReturnType<typeof useSelectFloatingInteractions>;
